import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbNavChangeEvent } from '@ng-bootstrap/ng-bootstrap/nav';
import { TranslateService } from '@ngx-translate/core';
import {
  PasConfirmationService,
  PasTabContentDirective,
  PasTabDirective,
  PasTabsetComponent,
  PasTabTitleDirective,
} from '@porsche-aftersales/frontend-toolbox';
import { AgGridModule } from 'ag-grid-angular';
import { Subscription } from 'rxjs';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { FieldMasterDataRoutingService } from '../../services/details-view-service/supplier-parts-routing.service';

import { PartsAgreementAddComponent } from './parts-agreements/parts-agreement-add.component';
import { PartsAgreementsComponent } from './parts-agreements/parts-agreements.component';
import { RecourseCriteriaComponent } from './recourse-criteria/recourse-criteria.component';
import { SupplierConditionComponent } from './supplier-conditions/supplier-condition.component';

/**
 * @deprecated somewhen get rid of this constants and try to unify with other places...
 * maybe split ids of menu entries and according sub menu entries.
 */
export enum FieldMasterDataPathIds {
  FieldMasterData = 'fieldMasterData', // id of menu entry
  PartsAgreements = 'partsAgreements', // id of sub menu entry
  AddPartsAgreement = 'addPartsAgreement', // id of sub menu entry
  RecourseCriteria = 'recourseCriteria', // id of sub menu entry
  SupplierCondition = 'supplierCondition', // id of sub menu entry
}

@Component({
  // intentionally no selector
  templateUrl: './field-master-data.component.html',
  styleUrls: ['./field-master-data.component.scss'],
  imports: [
    AgGridModule,
    TranslatePipe,
    PartsAgreementsComponent,
    PartsAgreementAddComponent,
    RecourseCriteriaComponent,
    SupplierConditionComponent,
    PasTabsetComponent,
    PasTabDirective,
    PasTabTitleDirective,
    PasTabContentDirective,
  ],
})
export class FieldMasterDataComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly fieldMasterDataRoutingService = inject(FieldMasterDataRoutingService);

  @ViewChild(PasTabsetComponent)
  public tabset: PasTabsetComponent;

  @ViewChild(PartsAgreementAddComponent)
  public partsAgreementAddComponent: PartsAgreementAddComponent;

  public readonly tabIds = FieldMasterDataPathIds;

  public addTabDisabledAndHidden = true;
  public routerParamName = 'tabId';
  private pathParamSubscription: Subscription;

  public ngOnInit(): void {
    this.getPathParams();
  }

  public onChangingTab(event: NgbNavChangeEvent): void {
    event.preventDefault();
  }

  public onBtnCancel(): void {
    this.preventTabChange();
  }

  public openTab(tabId: string): void {
    Promise.resolve(this.tabset?.tabs).then(() => {
      this.tabset.activeId = tabId;
      this.setTabVisibility(tabId);
      this.tabset.select(tabId);
      this.tabset.changeTabSize();
    });
  }

  public setTabVisibility(tabId: string): void {
    if (tabId === FieldMasterDataPathIds.PartsAgreements) {
      this.addTabDisabledAndHidden = true;
    }
    if (tabId === FieldMasterDataPathIds.AddPartsAgreement) {
      this.addTabDisabledAndHidden = false;
    }
  }

  public ngAfterViewInit(): void {
    this.cdRef.detectChanges(); // prevent angular error in dev mode
  }

  public ngOnDestroy(): void {
    this.pathParamSubscription.unsubscribe();
  }

  private getPathParams(): void {
    this.pathParamSubscription = this.route.params.subscribe((params: any) => {
      const tabId = params.tabId;
      this.initTabParams(tabId);
    });
  }

  private async initTabParams(tabId: string): Promise<void> {
    this.setTabVisibility(tabId);
    await Promise.resolve(this.tabset);
    if (this.tabset?.activeId === tabId) {
      if (this.partsAgreementAddComponent) {
        this.partsAgreementAddComponent.initializeView();
      }
    } else {
      this.openTab(tabId);
    }
  }

  private preventTabChange(): void {
    this.translateService
      .get(['FORM_CONTAINS_UNSAVED_DATA_TITLE', 'FORM_CONTAINS_UNSAVED_DATA_MESSAGE', 'OK', 'CANCEL'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.FORM_CONTAINS_UNSAVED_DATA_TITLE;
        const message: string = translate.FORM_CONTAINS_UNSAVED_DATA_MESSAGE;
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, false).then(() => {
          this.fieldMasterDataRoutingService.routeToTabPartsAgreement();
        });
      });
  }
}
